import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import LazyLoading from '../../../components/LazyLoading';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { toast } from 'react-toastify';
import useProductService from '../../../app/services/hooks/useProductService';
import { ProductDataForm, defaultProductDataForm } from '../../../app/models/Product';
import { Unit } from '../../../app/models/Unit';
import { ItemCategory } from '../../../app/models/ItemCategory';
import { Location } from '../../../app/models/Location';
import { Sector } from '../../../app/models/Sector';
import { Quality } from '../../../app/models/Quality';
import { Color } from '../../../app/models/Color';
import ProductFormContainer from './ProductFormContainer';

type SelectOpt = { id: number | string; name: string };

interface Props { productId: number; onSaved?: () => void; onCancel?: () => void; onError?: () => void; }

const NOOP = () => undefined;

const ProductEdit = ({ productId, onSaved, onCancel = NOOP, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { fetchingEditProduct, editProduct, fetchingUpdateProduct, updateProduct } = useProductService();

  const [product, setProduct] = useState<ProductDataForm>(defaultProductDataForm);
  const [units, setUnits] = useState<Unit[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [qualities, setQualities] = useState<Quality[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [ownershipTypes, setOwnershipTypes] = useState<SelectOpt[]>([]);
  const [taxes, setTaxes] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => { edit(); }, []);

  const edit = () => {
    showLoading?.('loading', 'Cargando producto...');
    editProduct(productId, {
      onSuccess: (res: ServiceResponse) => {
        setProduct(res.data.product);
        setUnits(res.data.units || []);
        setSectors(res.data.sectors || []);
        setCategories(res.data.categories || []);
        setLocations(res.data.locations || []);
        setQualities(res.data.qualities || []);
        setColors(res.data.colors || []);
        setOwnershipTypes(res.data.ownership_types || []);
        setTaxes(res.data.taxes || []);
        hideLoading?.();
      },
      onError: (res: ServiceResponse) => {
        hideLoading?.();
        toast.error(res.message);
        onError && onError();
      }
    });
  };

  const update = () => {
    showLoading?.('loading', 'Actualizando producto...');
    updateProduct(productId, product, {
      onSuccess: (res: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: res.message });
        onSaved && onSaved();
      },
      onError: (res: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: res.message });
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  return fetchingEditProduct ? (
    <LazyLoading height="300" />
  ) : (
    <ProductFormContainer
      fetching={fetchingUpdateProduct}
      action={update}
      cancel={onCancel}
      product={product}
      setProduct={setProduct}
      units={units}
      sectors={sectors}
      categories={categories}
      locations={locations}
      qualities={qualities}
      colors={colors}
      ownershipTypes={ownershipTypes}
      taxes={taxes}
      errorFields={errorFields}
      is_edit={true}
    />
  );
};

export default ProductEdit;
